/**
 * The admin panel's one way to geocode: every lookup goes to main-service (`api/geocoding.api`), which answers ZIPs
 * and "City, ST" from its locality table and falls back to vendors itself. Nothing here calls a geocoder.
 *
 * Lookups made in the same tick are sent as one request (up to `GEOCODING_BATCH_MAX`), so a map that places a
 * dozen stops at once costs one round trip, and a query already in flight is shared rather than asked twice.
 * No result cache: the server answers from a table, and an in-memory copy here would only go stale.
 */
import { GEOCODING_BATCH_MAX, type GeocodedPoint, type ResolvedLocality } from '@numeo/types'
import { geocodePointsApi, reverseLocalitiesApi } from 'api/geocoding.api'
import { log } from 'utils/logger'

type GeoPoint = { lat: number; lng: number }

/**
 * A ZIP resolution — the point plus the city/state the ZIP falls in, so a ZIP-entered stop reads like a city one.
 * `cityIsApproximate` says `city` is not a real mailing city (missing, or an administrative area such as a township
 * or county). Callers that only need a map point can ignore it; callers that write `city` into stored load data
 * must not persist an approximate name.
 */
type GeoPlace = { lat: number; lng: number; city: string; stateCode: string; cityIsApproximate: boolean }

/** A name that describes an administrative area rather than a mailing city. Word-bounded (Scarborough passes). */
const ADMIN_AREA_NAME = /\btownship\b|\bcounty\b|\bborough\b|\bparish\b|^town of\b/i

type Pending = { query: string; resolve: (point: GeocodedPoint | null) => void; reject: (error: unknown) => void }

const inFlight = new Map<string, Promise<GeocodedPoint | null>>()
let queue: Pending[] = []
let scheduled = false

async function flush(batch: Pending[]): Promise<void> {
    try {
        const { results } = await geocodePointsApi({ queries: batch.map((p) => p.query) })
        batch.forEach((p, i) => p.resolve(results[i] ?? null))
    } catch (error) {
        log.w('geocoding request failed', { queries: batch.length, error })
        batch.forEach((p) => p.reject(error))
    }
}

function drain(): void {
    scheduled = false
    const pending = queue
    queue = []
    for (let i = 0; i < pending.length; i += GEOCODING_BATCH_MAX) void flush(pending.slice(i, i + GEOCODING_BATCH_MAX))
}

/**
 * One query's point, batched with the others asked in this tick: null when nobody can place it, and a REJECTION when
 * the lookup itself failed (network, 429, 5xx), for a caller that tells the user "unavailable" apart from "not found".
 */
export function geocodeQueryStrict(query: string): Promise<GeocodedPoint | null> {
    const text = query.trim()
    if (!text) return Promise.resolve(null)
    const existing = inFlight.get(text)
    if (existing) return existing
    const promise = new Promise<GeocodedPoint | null>((resolve, reject) => {
        queue.push({ query: text, resolve, reject })
        if (!scheduled) {
            scheduled = true
            queueMicrotask(drain)
        }
    }).finally(() => inFlight.delete(text))
    inFlight.set(text, promise)
    return promise
}

/** `geocodeQueryStrict` for a caller that only needs a point or nothing: never rejects, a failure answers null. */
export const geocodeQuery = (query: string): Promise<GeocodedPoint | null> => geocodeQueryStrict(query).catch(() => null)

/** Resolve a US 5-digit ZIP to a point and the city/state it falls in. Null on a malformed ZIP or a miss. */
export async function geocodeZip(zip: string | null | undefined): Promise<GeoPlace | null> {
    const z = (zip ?? '').trim()
    if (!/^\d{5}$/.test(z)) return null
    const hit = await geocodeQuery(z)
    if (!hit || !Number.isFinite(hit.lat) || !Number.isFinite(hit.lng)) return null
    const city = hit.city?.trim() ?? ''
    const stateCode = hit.state?.trim().toUpperCase() ?? ''
    return { lat: hit.lat, lng: hit.lng, city, stateCode, cityIsApproximate: !city || ADMIN_AREA_NAME.test(city) }
}

/** Resolve "City, ST" (US) to a point. Null when the input is empty or nobody can place it. */
export async function geocodeCityState(city: string | null | undefined, state: string | null | undefined): Promise<GeoPoint | null> {
    const c = city?.trim() ?? ''
    const s = state?.trim() ?? ''
    if (!c && !s) return null
    const hit = await geocodeQuery(c && s ? `${c}, ${s}` : c || s)
    return hit ? { lat: hit.lat, lng: hit.lng } : null
}

/** Name the locality (country, state, city, ZIP) a point falls in. Null when nobody can; rejects on a request failure. */
export async function reverseLocality(point: GeoPoint): Promise<ResolvedLocality | null> {
    const { results } = await reverseLocalitiesApi({ points: [{ lat: point.lat, lng: point.lng }] })
    return results[0] ?? null
}
